import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectsTable = ({ projects, onProjectClick, onStatusUpdate }) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedProjects = React.useMemo(() => {
    if (!sortConfig?.key) return projects;

    return [...projects]?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];

      if (typeof aValue === 'string') {
        return sortConfig?.direction === 'asc'
          ? aValue?.localeCompare(bValue)
          : bValue?.localeCompare(aValue);
      }

      return sortConfig?.direction === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    });
  }, [projects, sortConfig]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'validado':
        return 'bg-success/10 text-success border-success/20';
      case 'en verificación':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'en desarrollo':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'pendiente':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getProjectTypeIcon = (type) => {
    const typeMap = {
      'A/R': 'TreePine',
      'IFM': 'Trees',
      'REDD+': 'Shield',
      'SOC': 'Sprout',
      'Ganadería Regenerativa': 'Beef'
    };
    return typeMap?.[type] || 'FolderTree';
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} className="opacity-40" />;
    }
    return (
      <Icon
        name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
        size={14}
        color="var(--color-primary)"
      />
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="table-base">
        <thead>
          <tr>
            <th className="w-12">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label="Seleccionar todos los proyectos"
              />
            </th>
            <th>
              <button
                onClick={() => handleSort('name')}
                className="flex items-center gap-2 font-semibold text-sm hover:text-primary transition-colors duration-250"
              >
                Proyecto
                <SortIcon columnKey="name" />
              </button>
            </th>
            <th>
              <button
                onClick={() => handleSort('country')}
                className="flex items-center gap-2 font-semibold text-sm hover:text-primary transition-colors duration-250"
              >
                País
                <SortIcon columnKey="country" />
              </button>
            </th>
            <th>
              <button
                onClick={() => handleSort('projectType')}
                className="flex items-center gap-2 font-semibold text-sm hover:text-primary transition-colors duration-250"
              >
                Tipo
                <SortIcon columnKey="projectType" />
              </button>
            </th>
            <th>
              <button
                onClick={() => handleSort('standard')}
                className="flex items-center gap-2 font-semibold text-sm hover:text-primary transition-colors duration-250"
              >
                Estándar
                <SortIcon columnKey="standard" />
              </button>
            </th>
            <th>
              <button
                onClick={() => handleSort('status')}
                className="flex items-center gap-2 font-semibold text-sm hover:text-primary transition-colors duration-250"
              >
                Estado
                <SortIcon columnKey="status" />
              </button>
            </th>
            <th className="text-right">
              <button
                onClick={() => handleSort('tco2ePerYear')}
                className="flex items-center gap-2 font-semibold text-sm hover:text-primary transition-colors duration-250 ml-auto"
              >
                tCO₂e/año
                <SortIcon columnKey="tco2ePerYear" />
              </button>
            </th>
            <th className="text-right">
              <button
                onClick={() => handleSort('erPerHa')}
                className="flex items-center gap-2 font-semibold text-sm hover:text-primary transition-colors duration-250 ml-auto"
              >
                ER/ha
                <SortIcon columnKey="erPerHa" />
              </button>
            </th>
            <th className="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {sortedProjects?.map((project) => (
            <tr
              key={project?.id}
              className="hover:bg-muted/50 transition-colors duration-250 cursor-pointer"
              onClick={() => onProjectClick(project)}
            >
              <td onClick={(e) => e?.stopPropagation()}>
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  aria-label={`Seleccionar ${project?.name}`}
                />
              </td>
              <td>
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
                    style={{ backgroundColor: 'var(--color-primary)15' }}
                  >
                    <Icon
                      name={getProjectTypeIcon(project?.projectType)}
                      size={16}
                      color="var(--color-primary)"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-foreground truncate">
                      {project?.name}
                    </div>
                    <div className="text-xs text-muted-foreground caption truncate">
                      ID: {project?.id}
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground">{project?.country}</span>
                </div>
              </td>
              <td>
                <span className="text-sm text-foreground">{project?.projectType}</span>
              </td>
              <td>
                <span className="text-sm text-foreground">{project?.standard}</span>
              </td>
              <td>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getStatusColor(
                    project?.status
                  )}`}
                >
                  {project?.status}
                </span>
              </td>
              <td className="text-right">
                <span className="text-sm font-medium data-text text-foreground">
                  {project?.tco2ePerYear?.toLocaleString('es-ES')}
                </span>
              </td>
              <td className="text-right">
                <span className="text-sm font-medium data-text text-foreground">
                  {project?.erPerHa?.toFixed(2)}
                </span>
              </td>
              <td onClick={(e) => e?.stopPropagation()}>
                <div className="flex items-center justify-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/project-detail-view?id=${project?.id}`)}
                    iconName="Eye"
                    iconSize={16}
                    title="Ver detalles"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onStatusUpdate(project)}
                    iconName="Edit"
                    iconSize={16}
                    title="Editar estado"
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProjectsTable;